import type { FC, FormEvent } from 'react'

import { useEffect, useState } from 'react'
import { MapPin, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

interface SearchBarProps {
    defaultQuery?: string
    defaultLocation?: string
    onSearch: (query: string, location: string) => void
    size?: 'default' | 'lg'
    className?: string
}

/** Keyword and location search used on the home page and the job explorer. */
export const SearchBar: FC<SearchBarProps> = ({ defaultQuery = '', defaultLocation = '', onSearch, size = 'default', className }) => {
    const [query, setQuery] = useState(defaultQuery)
    const [location, setLocation] = useState(defaultLocation)

    useEffect(() => setQuery(defaultQuery), [defaultQuery])
    useEffect(() => setLocation(defaultLocation), [defaultLocation])

    const submit = (event: FormEvent) => {
        event.preventDefault()
        onSearch(query.trim(), location.trim())
    }

    const large = size === 'lg'

    return (
        <form
            role='search'
            onSubmit={submit}
            className={cn(
                'flex flex-col gap-2 rounded-2xl border border-border bg-card p-2 shadow-card sm:flex-row sm:items-center sm:gap-0',
                large && 'shadow-lift sm:p-2.5',
                className
            )}
        >
            <label className='flex flex-1 items-center gap-2.5 px-3'>
                <Search className='size-5 shrink-0 text-muted-foreground' />
                <span className='sr-only'>Job title, skill or company</span>
                <input
                    value={query}
                    onChange={event => setQuery(event.target.value)}
                    placeholder='Job title, skill or company'
                    className={cn('w-full bg-transparent py-2 text-[0.95rem] outline-none placeholder:text-muted-foreground', large && 'py-3 text-base')}
                />
            </label>

            <div className='hidden h-8 w-px bg-border sm:block' />

            <label className='flex items-center gap-2.5 border-t border-border px-3 pt-2 sm:w-56 sm:border-t-0 sm:pt-0'>
                <MapPin className='size-5 shrink-0 text-muted-foreground' />
                <span className='sr-only'>Location</span>
                <input
                    value={location}
                    onChange={event => setLocation(event.target.value)}
                    placeholder='City, country or remote'
                    className={cn('w-full bg-transparent py-2 text-[0.95rem] outline-none placeholder:text-muted-foreground', large && 'py-3 text-base')}
                />
            </label>

            <Button type='submit' size={large ? 'lg' : 'default'} className={cn('sm:ml-2', large ? 'sm:px-7' : 'sm:px-5')}>
                Search
            </Button>
        </form>
    )
}
